import {CLStore} from 'common/src/redux/store';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import {addOrUpdateCardInputSchema} from '../../../../schemas/baseSchemas';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import standaloneAgent from 'common/src/modules/shared/copilot/utils/creators/standaloneAgent';
import {responseSchema} from '../../promptToCardAgentV2.schemas';
import getSetupAgentModel from '../../../../utils/getSetupAgentModel';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {cardTypeToName} from '../../../../campaignSetupAgent.config';
import getPromptToCardAgentInstructions from '../../../promptToCardAgent/utils/getPromptToCardAgentInstructions';
import setCardNameTool from '../general/setCardNameTool';
import setCardDescriptionTool from '../general/setCardDescriptionTool';
import addConditionOutputTool from './addConditionOutputTool';
import editConditionOutputTool from './editConditionOutputTool';
import getFilesTool from 'common/src/modules/shared/copilot/tools/getFilesTool';
import readValuesFromExcelTool from '../../../../tools/general/readValuesFromExcelTool/readValuesFromExcelTool';
import searchSignalTool from './searchSignalTool';
import deleteOutuputsTool from '../general/deleteOutputsTool';
import getCardDetailsTool from '../general/getCardDetailsTool';
import preProcessStandaloneAgent from '../../utils/general/preProcessStandaloneAgent';
import postProcessStandaloneAgent from '../../utils/general/postProcessStandaloneAgent';
import {ConditionCardScratchpadState} from '../../scratchpad/conditionCardScratchpad.types';
import setOperatorTool from './setOperatorTool';
import setSignalTool from './setSignalTool';
import getOrCreateConditionCard from '../../utils/conditions/getOrCreateConditionCard';
import searchFeedValuesTool from './searchFeedValuesTool';
import {FallbackOutputGuidelines} from '../../promptToCardAgentV2.consts';

const cardSpecificInstructions = `### What is a Condition card
A Condition card routes traffic based on a single signal — it evaluates the signal's value at ad-serve time and selects the matching output. The fallback output is selected when no regular output matches the signal's value.

### Condition card rules
${FallbackOutputGuidelines}.
- DO NOT add fallback outputs of any kind, only set the outputs requested by the user.

### Property Dependencies
- The signal must be discovered (searchSignal) before it can be set on the card, because setSignal requires a signal from the search results.
- The operator depends on the signal — each signal defines which operators are valid, so the signal must be set before choosing an operator.
- Output values depend on both the signal type and the chosen operator. For example, a numeric signal with a 'Between' operator requires a number range, while a text signal requires text values.
- If the signal has shouldQueryOptionalValues=true, feed values must be searched (searchFeedValues) before adding outputs, because the output values must come from the feed.
- Deleting outputs has no dependencies and can be done alongside any other tools.`;

export default function addOrUpdateConditionCardToolV2(store: CLStore) {
  async function execute(
    params: ToolExecuteParams<typeof addOrUpdateCardInputSchema, ConditionCardScratchpadState>,
  ) {
    const {toolUtils, callChain} = params;

    const {promptToUse, normalizedCardId, card} = await preProcessStandaloneAgent(
      params,
      store,
      getOrCreateConditionCard,
    );

    // Create the standalone agent definition
    const standaloneAgentDef = standaloneAgent({
      name: V2CardAgentToolName.AddOrUpdateConditionCard,
      role: `Your purpose is to convert a natural language prompt to a ${
        cardTypeToName[NodeType.CONDITION]
      } card`,
      instructions: getPromptToCardAgentInstructions({
        cardType: NodeType.CONDITION,
        cardId: normalizedCardId,
        cardSpecificInstructions,
      }),
      tools: [
        setCardNameTool(),
        setCardDescriptionTool(),
        deleteOutuputsTool(),
        getFilesTool(),
        getCardDetailsTool(),
        readValuesFromExcelTool(),

        //Condition tools
        searchSignalTool(store),
        setSignalTool(),
        setOperatorTool(),
        addConditionOutputTool(),
        editConditionOutputTool(),
        searchFeedValuesTool(),
      ],
      responseSchema,
      model: getSetupAgentModel(),
    });

    const toolScratchpad = {card};

    const result = await toolUtils.invokeStandaloneAgent({
      agent: standaloneAgentDef,
      prompt: promptToUse,
      callChain,
      scratchpad: toolScratchpad,
    });

    return postProcessStandaloneAgent({
      result: {status: result.status, chainOfThoughts: result.chainOfThoughts},
      scratchpad: toolScratchpad,
      store,
      cardId: normalizedCardId,
    });
  }

  return tool({
    name: V2CardAgentToolName.AddOrUpdateConditionCard,
    description:
      'Add or update a Condition card. Selects output based on parameter/signal, operator, and matching values per output. Supports reading values from feed columns or Excel.',
    displayName: 'Working on condition card',
    inputSchema: addOrUpdateCardInputSchema,
    execute,
  });
}
